import React, { useEffect, useState } from "react";
import { Phone, ArrowRight, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const PHONE_DISPLAY = "+43 664 328 95 99";
const PHONE_TEL = "+436643289599";
const DISMISS_KEY = "eco_callbar_dismissed";
const SHOW_AFTER = 500;   // px scrolled before showing

/**
 * Call-Bar: schmale Leiste am unteren Rand, erscheint beim Scrollen.
 * Mobil: große Telefon-Tap-Zone. Desktop: Telefon + Anfrage-CTA.
 * Dismissible pro Session (localStorage) und nicht sichtbar auf /kontakt + /admin/*
 */
export default function CallBar() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const location = useLocation();

  // Hide on contact page itself and in admin area
  const isHidden = /^\/(kontakt|admin|checkout)/.test(location.pathname);

  useEffect(() => {
    try {
      setDismissed(sessionStorage.getItem(DISMISS_KEY) === "1");
    } catch {}
  }, []);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, "1"); } catch {}
  };

  if (isHidden || dismissed) return null;

  return (
    <div
      aria-hidden={!visible}
      data-testid="call-bar"
      className={`fixed left-0 right-0 bottom-0 z-40 transition-transform duration-500 ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="bg-[#0F2A48]/95 backdrop-blur-xl border-t border-[#5FA637]/40 shadow-[0_-10px_40px_rgba(0,0,0,0.4)]">
        <div className="site-container py-3 flex items-center gap-3 md:gap-6">
          {/* Pulsing dot */}
          <div className="hidden sm:flex items-center gap-2 shrink-0">
            <span className="relative inline-flex w-2.5 h-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#5FA637] opacity-60 animate-ping"/>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#5FA637]"/>
            </span>
            <span className="text-[10px] uppercase tracking-[0.18em] text-white/70 font-semibold">
              Projekt besprechen
            </span>
          </div>

          <div className="hidden md:block h-6 w-px bg-white/15"/>

          {/* Phone link — primary tap target */}
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="call-bar-phone"
            className="flex items-center gap-3 group flex-1 min-w-0"
          >
            <div className="w-10 h-10 md:w-11 md:h-11 bg-[#5FA637] text-[#0A1A2E] flex items-center justify-center shrink-0 group-hover:bg-[#7DBE4E] transition-colors">
              <Phone size={16} strokeWidth={2.2}/>
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.15em] text-[#5FA637] font-semibold">
                Jetzt anrufen
              </div>
              <div className="font-display text-sm md:text-base text-white truncate group-hover:text-[#5FA637] transition-colors">
                {PHONE_DISPLAY}
              </div>
            </div>
          </a>

          {/* Desktop secondary CTA */}
          <Link
            to="/kontakt"
            data-testid="call-bar-inquiry"
            className="hidden lg:inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase font-semibold px-4 py-2.5 border border-white/25 hover:border-[#5FA637] hover:text-[#5FA637] text-white/85 whitespace-nowrap transition-colors"
          >
            Projekt anfragen <ArrowRight size={13}/>
          </Link>

          {/* Mobile short CTA */}
          <Link
            to="/kontakt"
            data-testid="call-bar-inquiry-mobile"
            className="lg:hidden shrink-0 inline-flex items-center justify-center text-[10px] tracking-[0.12em] uppercase font-semibold px-3 py-2 border border-white/25 hover:border-[#5FA637] text-white/85 whitespace-nowrap"
          >
            Anfragen
          </Link>

          {/* Dismiss */}
          <button
            onClick={dismiss}
            data-testid="call-bar-dismiss"
            aria-label="Call-Bar schließen"
            className="w-8 h-8 flex items-center justify-center text-white/40 hover:text-white shrink-0"
          >
            <X size={14}/>
          </button>
        </div>
      </div>
    </div>
  );
}
